import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { ArrowRight, FileText, Save, Settings, X } from "lucide-react";

interface InternalColumnMapping {
  id: string;
  targetField: string;
  sourceColumn: string;
  confidence: number;
  transformation: string;
}

interface SaveTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (templateName: string, action: "create" | "update") => void;
  mappings: InternalColumnMapping[];
  fileName: string;
  existingTemplateName?: string;
  hasUnsavedChanges?: boolean;
}

const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  mappings,
  fileName,
  existingTemplateName,
  hasUnsavedChanges = false,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [saveAction, setSaveAction] = useState<"create" | "update">("create");

  const handleSave = () => {
    if (templateName.trim()) {
      onSave(templateName.trim(), saveAction);
      setTemplateName("");
      setSaveAction("create");
      onClose();
    }
  };

  const handleUpdateExisting = () => {
    if (existingTemplateName) {
      onSave(existingTemplateName, "update");
      onClose();
    }
  };

  const getTransformationLabel = (transformation: string) => {
    const labels: Record<string, string> = {
      none: "No Transformation",
      no_transformation: "No Transformation",
      case_lower: "Lowercase",
      case_upper: "Uppercase",
      merge: "Merge Fields",
      split: "Split Field",
      format: "Format Data",
      clean: "Clean Data",
    };
    return labels[transformation] || transformation;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 95) return "bg-green-100 text-green-800 border-green-200";
    if (confidence >= 75)
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-red-100 text-red-800 border-red-200";
  };

  const validMappings = mappings.filter((m) => m.sourceColumn);
  const transformationsCount = mappings.filter(
    (m) =>
      m.transformation &&
      m.transformation !== "none" &&
      m.transformation !== "no_transformation"
  ).length;
  const isUpdateMode = hasUnsavedChanges && existingTemplateName;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        {/* Overlay - Dark background */}
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Modal Content */}
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <Dialog.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Dialog.Close>

          {/* Header */}
          <div className="space-y-2">
            <Dialog.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
              <Save className="h-5 w-5" />
              {isUpdateMode ? "Update Template" : "Save Mapping Template"}
            </Dialog.Title>
            <Dialog.Description className="text-sm text-gray-500">
              {isUpdateMode
                ? `Update the existing template "${existingTemplateName}" with your recent changes, or create a new template.`
                : "Save this mapping configuration as a template for future use with similar file structures."}
            </Dialog.Description>
          </div>

          <div className="space-y-6">
            {/* Update Options */}
            {isUpdateMode && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-amber-800">
                      You have unsaved changes to your template. What would you
                      like to do?
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUpdateExisting}
                        className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        <Save className="h-3 w-3" />
                        Update "{existingTemplateName}"
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSaveAction("create")}
                        className="gap-2"
                      >
                        Create New Template
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Template Name Input */}
            {(!isUpdateMode || saveAction === "create") && (
              <div className="space-y-2">
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  placeholder="Enter a descriptive name for this template"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full"
                />
              </div>
            )}

            {/* File Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Source File Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Original File:</span>
                  <span className="font-medium">{fileName || "Unknown"}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Total Mappings:</span>
                  <span className="font-medium">
                    {validMappings.length} fields mapped
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Transformations:</span>
                  <span className="font-medium">
                    {transformationsCount} applied
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Mapping Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">
                  Mapping Configuration Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {validMappings.map((mapping) => (
                    <div
                      key={mapping.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-700">
                            {mapping.sourceColumn}
                          </span>
                          <ArrowRight className="h-3 w-3 text-gray-400" />
                          <span className="text-sm font-medium text-green-700">
                            {mapping.targetField}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {mapping.transformation &&
                          mapping.transformation !== "none" &&
                          mapping.transformation !== "no_transformation" && (
                            <div className="flex items-center gap-1">
                              <Settings className="h-3 w-3 text-purple-600" />
                              <Badge
                                variant="outline"
                                className="text-xs text-purple-700 border-purple-200"
                              >
                                {getTransformationLabel(mapping.transformation)}
                              </Badge>
                            </div>
                          )}
                        <Badge
                          className={`text-xs ${getConfidenceColor(
                            mapping.confidence
                          )}`}
                          variant="outline"
                        >
                          {mapping.confidence}% confidence
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Template Benefits */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm">
                <div className="font-medium text-blue-900 mb-2">
                  Template Benefits:
                </div>
                <ul className="text-blue-800 space-y-1 text-xs">
                  <li>
                    • Reuse this configuration for files with similar column
                    structures
                  </li>
                  <li>
                    • Save time by automatically applying proven mappings and
                    transformations
                  </li>
                  <li>• Ensure consistency across multiple data imports</li>
                  <li>
                    • Reduce errors by using tested mapping configurations
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {(!isUpdateMode || saveAction === "create") && (
              <Button
                onClick={handleSave}
                disabled={!templateName.trim()}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                Save Template
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SaveTemplateDialog;
